import { FC, FocusEvent } from "react";

import { Flex, Spinner, useBreakpointValue } from "@chakra-ui/react";

import Icon from "@components/Icon";
import TextInput from "@components/TextInput";
import { useAutoCompleteContext } from "@lib/choc-autocomplete";
import debounce from "@utils/debounce";

import { TSelectInputProps } from "./types";

const SelectInput: FC<TSelectInputProps> = ({ isLoading, isReadOnly, ...rest }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { getInputProps } = useAutoCompleteContext();
  const inputProps = getInputProps(rest);

  const loader = (
    <Flex align="center" mx="0.5rem">
      <Spinner size="md" />
    </Flex>
  );

  /**
   * removeReadOnly, handleOnFocus, handleOnBlur
   * Has additional logic to avoid auto suggestion in field, it's related to
   * chromium engine issue:
   * https://bugs.chromium.org/p/chromium/issues/detail?id=1243301
   * So in this case by the default our field is always readOnly untill we
   * focus it, this one helps to prevent showing suggestions.
   */
  const removeReadOnly = debounce((event) => {
    event.target.removeAttribute("readonly");
    event.target.removeAttribute("aria-readonly");
    event.target.focus();
  }, 100);

  const handleOnFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (inputProps.input.onFocus) {
      inputProps.input.onFocus(event);
    }

    if (!isReadOnly && !isMobile) {
      removeReadOnly(event);
    }
  };

  const handleOnBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (inputProps.input.onBlur) {
      inputProps.input.onBlur(event);
    }

    if (!isReadOnly && !isMobile) {
      event.target.setAttribute("readonly", "true");
      event.target.setAttribute("aria-readonly", "true");
    }
  };

  const searchIcon = <Icon icon="magnifier" size="1.5rem" />;
  return (
    <TextInput
      {...inputProps.input}
      isFormik={false}
      rightAddon={isLoading ? loader : searchIcon}
      rightSpacing="0.5rem"
      rightAddonOffset="2.75rem"
      isReadOnly={!isMobile}
      onFocus={handleOnFocus}
      onBlur={handleOnBlur}
      // because off doesn't fully prevent autocomplete
      autoComplete="none"
      autoFocus={false}
      role="combobox"
      spellCheck="false"
      autoCapitalize="none"
      autoCorrect="off"
    />
  );
};
export default SelectInput;
